// Farm presentation follows real working crews and the actual harvest buffer.
// Crop growth is a visual work cycle, not a second food-production timer.
import { Group, Mesh, type Material } from 'three'
import { BUFFER_CAP } from '../../shared/constants'
import type { Building } from '../../shared/types'
import type { GameState } from '../state'
import { FARM_FIELDS, FARM_SOIL_Y, farmBaseGeometry, farmCropGeometry, farmHarvestGeometry } from './models3d/farm3d'

export class FarmVisual extends Group {
  private crops: Mesh[] = []
  private stages: number[] = []
  private grain: Mesh
  private work: number
  private lastFood: number | undefined
  private pulse = 0

  constructor(private seed: number, color: string, material: Material) {
    super()
    const base = new Mesh(farmBaseGeometry(color, seed), material)
    base.castShadow = base.receiveShadow = true
    this.add(base)
    this.work = (seed % 11) * 4.1
    for (let i = 0; i < FARM_FIELDS.length; i++) {
      const crop = new Mesh(farmCropGeometry(2, seed, i), material)
      crop.castShadow = crop.receiveShadow = true
      crop.matrixAutoUpdate = false
      this.crops.push(crop)
      this.stages.push(-1)
      this.add(crop)
    }
    this.grain = new Mesh(farmHarvestGeometry(), material)
    this.grain.position.set(1.4, FARM_SOIL_Y + 2.5, -10.8)
    this.grain.castShadow = true
    this.grain.visible = false
    this.add(this.grain)
  }

  update(st: GameState, b: Building, dt: number, clock: number, reducedMotion: boolean): boolean {
    const step = Math.max(0, Math.min(.1, dt))
    const fresh = st.connected && Date.now() - st.net.lastTickAt < 3000
    const room = BUFFER_CAP - Object.values(b.buffer).reduce((sum, n) => sum + (n ?? 0), 0)
    const hands = b.workers.filter(id => {
      const u = st.units.get(id)
      return u && u.hp > 0 && u.job === b.id && u.state === 'working'
    }).length
    const working = b.done && !b.paused && fresh && hands > 0 && room > .001
    if (working && !reducedMotion) this.work += step * Math.min(1, hands / 2)

    let changed = false
    for (let i = 0; i < this.crops.length; i++) {
      const progress = ((this.work / 48 + i / 3) % 1 + 1) % 1
      const stage = Math.min(2, Math.floor(progress * 3)) as 0 | 1 | 2
      const crop = this.crops[i]
      if (stage !== this.stages[i]) {
        crop.geometry = farmCropGeometry(stage, this.seed, i)
        this.stages[i] = stage
        changed = true
      }
      const { x, z } = FARM_FIELDS[i]
      const wave = reducedMotion ? 0 : Math.sin(clock * 1.6 + this.seed * .7 + i * 1.8)
      const grow = .8 + (progress * 3 - stage) * .2
      // Shear from the soil upwards: rooted stems sway, whole fields never slide.
      crop.matrix.set(1, wave * .075, 0, x, 0, grow, 0, FARM_SOIL_Y,
        0, wave * .04, 1, z, 0, 0, 0, 1)
      crop.matrixWorldNeedsUpdate = true
    }

    const food = Math.max(0, b.buffer.food ?? 0)
    if (working && this.lastFood !== undefined && Math.floor(food) > Math.floor(this.lastFood)) this.pulse = 1
    this.lastFood = food
    this.pulse = Math.max(0, this.pulse - step * 1.8)
    const visible = food >= 1
    if (this.grain.visible !== visible) changed = true
    this.grain.visible = visible
    const fill = Math.min(1, food / 12)
    this.grain.scale.set(1, .3 + fill * .7, 1)
    this.grain.position.y = FARM_SOIL_Y + 2.5 + (reducedMotion ? 0 : Math.sin(this.pulse * Math.PI) * 1.1)
    return changed
  }
}
